/** Compact result presentation. Identities, values, source display and recommendation states are supplied by Web. */
import type { ReactNode } from 'react'
import type { ShopifyResultTreatmentCard } from '@testfeed/shopify-contracts'

function resultCardTone(card: ShopifyResultTreatmentCard): 'success' | 'warning' | 'auto' {
  if (card.isRecommended) return 'success'
  if (card.state.icon === 'next' && card.badge?.tone === 'warning') return 'warning'
  return 'auto'
}

export function ServerResultCard({ card, actions }: { card: ShopifyResultTreatmentCard; actions?: ReactNode }) {
  const stateBadge = card.badge ? (
    <div className="tf-server-result-verdict">
      <s-badge tone={card.badge.tone}>{card.badge.text}</s-badge>
    </div>
  ) : null
  const tone = resultCardTone(card)
  let frameClass = 'tf-server-result-frame'
  if (tone === 'success') frameClass += ' tf-server-result-frame--recommended'
  else if (tone === 'warning') frameClass += ' tf-server-result-frame--caution'
  const content = (
    <div className="tf-server-result-card">
      <div className="tf-server-result-header">
        <s-stack direction="inline" gap="small-200" justifyContent="space-between" alignItems="start">
          <s-heading>{card.label}</s-heading>
          <s-stack direction="inline" gap="small-100" justifyContent="end">
            {card.badges.map((label) => (
              <s-badge key={label} tone="info">
                {label}
              </s-badge>
            ))}
          </s-stack>
        </s-stack>
      </div>
      <div className="tf-server-result-content">
        <ServerResultContent content={card.content} />
        <ServerResultMetadata card={card} />
      </div>
      <ServerResultMetric card={card} />
      <div className="tf-server-result-actions">{actions}</div>
    </div>
  )
  return (
    <div className={frameClass}>
      <s-section>{content}</s-section>
      {stateBadge}
    </div>
  )
}

function ServerResultContent({ content }: { content: ShopifyResultTreatmentCard['content'] }) {
  switch (content.kind) {
    case 'price': {
      const detail = content.detail ? <s-text color="subdued">{content.detail}</s-text> : null
      return (
        <s-stack direction="block" gap="small-200">
          <p className="tf-server-result-price">{content.value}</p>
          {detail}
        </s-stack>
      )
    }
    case 'text':
      return (
        <s-stack direction="block" gap="small-200">
          <p className="tf-server-result-text">
            {content.segments.map((segment, index) => (
              <ContentSegment key={index} segment={segment} />
            ))}
          </p>
          <s-text color="subdued">{content.detail}</s-text>
        </s-stack>
      )
    case 'photo':
      return <img className="tf-server-result-photo" src={content.src} alt={content.alt} />
    case 'unavailable':
      return <s-paragraph>{content.message}</s-paragraph>
  }
}

function ContentSegment({ segment }: { segment: { text: string; highlighted: boolean } }) {
  if (segment.highlighted) return <mark className="tf-server-result-highlight">{segment.text}</mark>
  return <span>{segment.text}</span>
}

function ServerResultMetric({ card }: { card: ShopifyResultTreatmentCard }) {
  const progress =
    card.metricProgress !== null ? (
      <s-progress
        value={card.metricProgress}
        max={100}
        tone={resultCardTone(card)}
        accessibilityLabel={card.metricAccessibilityLabel}
      />
    ) : null
  return (
    <div className="tf-server-result-score">
      <p className="tf-server-result-metric">{card.metricValue}</p>
      <s-text>{card.metricLabel}</s-text>
      {progress}
    </div>
  )
}

function ServerResultMetadata({ card }: { card: ShopifyResultTreatmentCard }) {
  if (!card.wordCountLabel && card.notices.length === 0) return null
  const wordCount = card.wordCountLabel ? <s-text color="subdued">{card.wordCountLabel}</s-text> : null
  return (
    <div className="tf-server-result-metadata">
      <s-stack direction="block" gap="small-100">
        {wordCount}
        {card.notices.map((text) => (
          <s-text key={text} tone="warning">
            {text}
          </s-text>
        ))}
      </s-stack>
    </div>
  )
}
